import React from 'react';

const PassageSettingsModal = ({ isOpen, onClose, settings, onSettingsChange, onConfirm, title = "지문 생성 설정", isGenerating = false, generationProgress = null }) => {
  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    onSettingsChange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {isGenerating ? '지문 생성 중...' : title}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* 학교급 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              학교급
            </label>
            <select
              value={settings.schoolLevel}
              onChange={(e) => handleInputChange('schoolLevel', e.target.value)}
              disabled={isGenerating}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">선택하세요</option>
              <option value="elementary">초등학교</option>
              <option value="middle">중학교</option>
              <option value="high">고등학교</option>
              <option value="advanced">심화과정</option>
            </select>
          </div>

          {/* 학년 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              학년
            </label>
            <select
              value={settings.grade}
              onChange={(e) => handleInputChange('grade', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!settings.schoolLevel || isGenerating}
            >
              <option value="">선택하세요</option>
              {settings.schoolLevel === 'elementary' && (
                <>
                  <option value="3">3학년</option>
                  <option value="4">4학년</option>
                  <option value="5">5학년</option>
                  <option value="6">6학년</option>
                </>
              )}
              {settings.schoolLevel === 'middle' && (
                <>
                  <option value="1">1학년</option>
                  <option value="2">2학년</option>
                  <option value="3">3학년</option>
                </>
              )}
              {settings.schoolLevel === 'high' && (
                <>
                  <option value="1">1학년</option>
                  <option value="2">2학년</option>
                  <option value="3">3학년</option>
                </>
              )}
              {settings.schoolLevel === 'advanced' && (
                <>
                  <option value="1">1단계</option>
                  <option value="2">2단계</option>
                  <option value="3">3단계</option>
                </>
              )}
            </select>
          </div>

          {/* 주제 입력 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              지문 주제
            </label>
            <input
              type="text"
              value={settings.topic}
              onChange={(e) => handleInputChange('topic', e.target.value)}
              placeholder="예: 환경보호, 건강한 생활, 우정 등"
              disabled={isGenerating}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* 지문 마스터 시트 URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              지문 마스터 시트 URL (선택사항)
            </label>
            <input
              type="url"
              value={settings.passageSheetUrl}
              onChange={(e) => handleInputChange('passageSheetUrl', e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              disabled={isGenerating}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">
              생성된 지문을 저장할 구글시트 URL을 입력하세요 (기존 학습로그와 동일한 방식)
            </p>
          </div>

          {/* 설명 - 생성 중이 아닐 때만 표시 */}
          {!isGenerating && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">지문 생성 안내</p>
                  <ul className="space-y-1 text-blue-700">
                    <li>• 선택한 학교급에 맞는 어휘를 90% 이상 사용합니다</li>
                    <li>• 초등 3-4학년(base1), 5-6학년(base2), 중등 1-3학년(base3-5), 고등 1-3학년(base6-8), 심화(base9)</li>
                    <li>• 약 300단어 내외의 지문이 생성됩니다</li>
                    <li>• 텍스트 파일과 HTML 워크시트가 생성됩니다</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* 진행상황 표시 */}
          {isGenerating && generationProgress && (
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500 mr-2"></div>
                <span className="font-medium text-green-800">생성 진행중...</span>
              </div>
              
              <div className="space-y-3">
                {generationProgress.steps.map((step, index) => (
                  <div key={index} className="flex items-center">
                    <div className={`w-4 h-4 rounded-full mr-3 flex items-center justify-center ${
                      step.status === 'completed' ? 'bg-green-500' :
                      step.status === 'current' ? 'bg-blue-500 animate-pulse' :
                      'bg-gray-300'
                    }`}>
                      {step.status === 'completed' && (
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {step.status === 'current' && (
                        <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                      )}
                    </div>
                    <span className={`text-sm ${
                      step.status === 'completed' ? 'text-green-700' :
                      step.status === 'current' ? 'text-blue-700 font-medium' :
                      'text-gray-500'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>

              {generationProgress.currentStep && (
                <div className="mt-3 text-xs text-gray-600 bg-white p-2 rounded border">
                  {generationProgress.currentStep}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={!settings.schoolLevel || !settings.grade || !settings.topic || isGenerating}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {isGenerating && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            )}
            {isGenerating ? '생성 중...' : '생성하기'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PassageSettingsModal;